import { FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { api, ApiError } from '../api/client.js';
import type { OrgInvitation, OrgMember, OrgRole } from '../api/client.js';
import { useI18n, useT } from '../state/i18n.js';
import type { Lang } from '../state/i18n.js';

// 워크스페이스(org) 멤버·초대 패널 — Workspace.tsx 가 임베드한다.
// 축 주의: 여기 역할은 "워크스페이스 축"(OWNER/ADMIN/MEMBER). 문서함(폴더) 역할과 다른 축이다.
//
// 서버 계약(실측, apps/api/src/modules/orgs/orgs.service.ts):
//  - 멤버/초대 조회·변경 전부 org ADMIN 이상 — 아니면 403 'Insufficient role.'
//    → 비ADMIN 은 애초에 조회하지 않는다(뻔한 403 을 에러 배너로 오독시키지 않기 위해).
//  - OWNER 승격·강등·제거는 OWNER 만, 마지막 OWNER 강등·제거는 서버가 400 으로 차단.
//    여기 disabled 는 UX 힌트일 뿐 — 최종 판정과 문구는 서버 것을 그대로 노출한다.
//  - 초대 role 은 자기보다 높게 못 준다(동급까지 허용) — 옵션 목록이 그 규칙을 미러한다.
//  - ★메일 발송기가 없다(apps/api 전체에 mailer 부재). 초대 = OrgInvitation 행 생성이 전부.
//    수신자는 그 이메일로 가입/로그인하고 이메일 인증을 마치면 리컨실이 자동 합류시킨다
//    (org-invite-reconcile.ts). 그래서 "메일 발송됨"으로 읽히는 표기는 금지하고,
//    사람이 직접 전달할 가입 안내 문구를 만들어 복사할 수 있게 한다 — 있는 척 금지.
//  - 초대 철회 409 = 그 사이 이미 수락됨 → 서버 문구 그대로 보여주고 목록을 재조회한다
//    (이제 멤버 목록에 있으니 제거는 멤버 행에서).

type Dict = { ko: string; en: string };

const ROLE_LABEL: Record<OrgRole, Dict> = {
  OWNER: { ko: '소유자', en: 'Owner' },
  ADMIN: { ko: '관리자', en: 'Admin' },
  MEMBER: { ko: '멤버', en: 'Member' },
};
const ROLE_OPTIONS: readonly OrgRole[] = ['OWNER', 'ADMIN', 'MEMBER'] as const;
// api/common/roles.ts ORG_RANK 미러 — 역할 문자열 자체는 client.ts OrgRole(=common/roles.ts) 경유.
const ORG_RANK: Record<OrgRole, number> = { MEMBER: 1, ADMIN: 2, OWNER: 3 };

type Phase = 'loading' | 'ready' | 'error' | 'forbidden';

/** 서버 메시지를 그대로 쓴다(마지막 OWNER 가드 등 서버 정책 문구를 삼키지 않기 위해). */
function errText(err: unknown, fallback: string): string {
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

/** 초대받은 사람이 실제로 합류하는 절차 그대로 — 리컨실(org-invite-reconcile.ts) 동작의 서술이다.
 *  메일이 나가지 않으므로 이 문구를 사람이 직접(슬랙·메신저 등) 전달해야 한다.
 *  ★React 트리 밖(단독 유닛 테스트 등)에서도 호출되므로 useT() 훅 대신 lang 을 인자로 받는다.
 *    기본값 'ko' 는 이 함수의 기존 동작(및 그 단독 테스트)을 그대로 보존하기 위함이다. */
export function inviteGuideText(email: string, orgName?: string, subdomain?: string, lang: Lang = 'ko'): string {
  const pick = <T,>(dict: { ko: T; en: T }): T => dict[lang];
  const lines = [
    pick({
      ko: `[cloudbtl] ${orgName || subdomain || '워크스페이스'} 초대 안내`,
      en: `[cloudbtl] Invitation to ${orgName || subdomain || 'the workspace'}`,
    }),
    pick({
      ko: `1) https://cloudbtl.com/login 에서 ${email} 계정으로 가입(또는 로그인)하세요.`,
      en: `1) Sign up (or sign in) at https://cloudbtl.com/login with the ${email} account.`,
    }),
    pick({
      ko: `2) 이메일 인증을 마치면 워크스페이스에 자동으로 합류됩니다.`,
      en: `2) Once you verify your email, you'll automatically join the workspace.`,
    }),
  ];
  if (subdomain) {
    lines.push(
      pick({
        ko: `3) 자료실 주소: https://${subdomain}.cloudbtl.com/documents`,
        en: `3) Library address: https://${subdomain}.cloudbtl.com/documents`,
      }),
    );
  }
  lines.push(
    pick({
      ko: `※ 이 초대는 ${email} 주소로 로그인할 때만 적용됩니다.`,
      en: `※ This invitation applies only when signing in with the ${email} address.`,
    }),
  );
  return lines.join('\n');
}

export function OrgMembers({
  orgId,
  myRole,
  myUserId,
  orgName,
  subdomain,
  onError,
}: {
  orgId: string;
  myRole: OrgRole;
  /** 본인 행 판정(제거 버튼을 '탈퇴'로 표기). 없으면 전부 '제거'로 표기될 뿐 동작은 같다. */
  myUserId?: string | null;
  /** 초대 안내문 조립용 — 없으면 해당 줄만 빠진다. */
  orgName?: string;
  subdomain?: string;
  onError?: (message: string) => void;
}): JSX.Element {
  const t = useT();
  const { lang } = useI18n();
  const isAdmin = ORG_RANK[myRole] >= ORG_RANK.ADMIN;
  const isOwner = myRole === 'OWNER';
  const [members, setMembers] = useState<OrgMember[]>([]);
  const [invitations, setInvitations] = useState<OrgInvitation[]>([]);
  // 비ADMIN 은 조회 자체를 하지 않으므로 '불러오는 중…' 이 한 프레임도 뜨지 않게 초기값부터 forbidden.
  const [phase, setPhase] = useState<Phase>(isAdmin ? 'loading' : 'forbidden');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<OrgRole>('MEMBER');
  /** 화면에 펼쳐진 가입 안내문(초대 직후 자동, pending 행에서 수동). */
  const [guide, setGuide] = useState<{ email: string; text: string } | null>(null);
  const [copied, setCopied] = useState(false);

  // onError 는 호출자가 인라인 람다로 줄 수 있다 — deps 에 넣으면 load 가 매 렌더 재생성돼
  // 마운트 이펙트가 무한 재실행된다. ref 로 최신 참조만 들고 간다.
  const onErrorRef = useRef(onError);
  useEffect(() => {
    onErrorRef.current = onError;
  });

  // orgId 가 빠르게 바뀔 때 늦게 도착한 응답이 새 org 목록을 덮어쓰지 않도록.
  const reqSeq = useRef(0);

  const fail = useCallback((message: string) => {
    setError(message);
    onErrorRef.current?.(message);
  }, []);

  const load = useCallback(async () => {
    const seq = ++reqSeq.current;
    setError(null);
    setNotice(null);
    // ★비ADMIN 은 호출하지 않는다 — 서버는 어차피 403 'Insufficient role.' 을 준다.
    if (!isAdmin) {
      setMembers([]);
      setInvitations([]);
      setPhase('forbidden');
      return;
    }
    setPhase('loading');
    try {
      const [m, inv] = await Promise.all([api.orgMembers(orgId), api.orgInvitations(orgId)]);
      if (seq !== reqSeq.current) return;
      setMembers(m.members);
      setInvitations(inv.invitations);
      setPhase('ready');
    } catch (err) {
      if (seq !== reqSeq.current) return;
      // myRole 이 낡았거나(강등·탈퇴 직후) 서버가 404 를 쓰는 경우 — 둘 다 "권한 없음"으로 해석.
      if (err instanceof ApiError && (err.status === 403 || err.status === 404)) {
        setMembers([]);
        setInvitations([]);
        setPhase('forbidden');
        return;
      }
      setPhase('error');
      fail(errText(err, t({ ko: '멤버 목록을 불러오지 못했습니다.', en: 'Could not load the member list.' })));
    }
  }, [orgId, isAdmin, fail, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const locked = busy || phase === 'loading';
  // 서버 규칙 미러: 자기보다 높은 role 은 초대 옵션에서 제외(동급까지) — 최종 판정은 서버.
  const invitableRoles = ROLE_OPTIONS.filter((r) => ORG_RANK[r] <= ORG_RANK[myRole]);

  function openGuide(email: string): void {
    setGuide({ email, text: inviteGuideText(email, orgName, subdomain, lang) });
    setCopied(false);
  }

  async function copyGuide(): Promise<void> {
    if (!guide) return;
    try {
      await navigator.clipboard.writeText(guide.text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard 권한 거부 등 — textarea 가 그대로 있으니 수동 복사 가능. 실패를 에러로 승격하지 않는다.
    }
  }

  async function submitInvite(event: FormEvent): Promise<void> {
    event.preventDefault();
    const email = inviteEmail.trim();
    if (!email || busy) return;
    const role = inviteRole;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await api.inviteOrgMember(orgId, email, role);
      setInviteEmail('');
      setInviteRole('MEMBER');
      await load(); // 낙관적 갱신 대신 재조회 — pending 판정·재초대 병합은 서버가 한다
      // load() 가 notice 를 비우므로 그 뒤에 세운다. ★메일은 나가지 않는다(서버에 mailer 없음).
      setNotice(
        t({
          ko: `${email} 을(를) ${t(ROLE_LABEL[role])} 로 초대했습니다. 초대 메일은 아직 발송되지 않습니다 — 아래 안내 문구를 복사해 직접 전달하세요.`,
          en: `Invited ${email} as ${t(ROLE_LABEL[role])}. The invitation email hasn't been sent yet — copy the guide text below and deliver it yourself.`,
        }),
      );
      openGuide(email);
    } catch (err) {
      fail(errText(err, t({ ko: '초대에 실패했습니다.', en: 'Could not send the invitation.' })));
    } finally {
      setBusy(false);
    }
  }

  async function changeRole(member: OrgMember, next: OrgRole): Promise<void> {
    if (next === member.role || busy) return;
    if (member.userId === myUserId && ORG_RANK[next] < ORG_RANK[member.role]) {
      // 본인 강등 = 이 관리 화면 접근 자체를 잃을 수 있다 — 복구는 다른 관리자 손에 넘어간다.
      const ok = window.confirm(
        t({
          ko: `본인 역할을 ${t(ROLE_LABEL[next])} 로 낮추면 이 관리 화면을 더 사용하지 못할 수 있습니다.\n계속할까요?`,
          en: `Lowering your own role to ${t(ROLE_LABEL[next])} may lock you out of this management screen.\nContinue?`,
        }),
      );
      if (!ok) return;
    }
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await api.updateOrgMember(orgId, member.id, next);
      await load();
    } catch (err) {
      fail(errText(err, t({ ko: '역할 변경에 실패했습니다.', en: 'Could not change the role.' })));
      await load(); // select 표시를 서버 상태로 되돌린다
    } finally {
      setBusy(false);
    }
  }

  async function removeMember(member: OrgMember): Promise<void> {
    if (busy) return;
    const self = member.userId === myUserId;
    const ok = window.confirm(
      self
        ? t({
            ko: '이 워크스페이스에서 탈퇴할까요?\n다시 들어오려면 다른 관리자의 초대가 필요합니다.',
            en: 'Leave this workspace?\nYou will need another admin to invite you back in.',
          })
        : t({
            ko: `${member.email} 님을 이 워크스페이스에서 제거할까요?\n이 사람 앞으로 남아 있는 초대도 함께 무효화됩니다.`,
            en: `Remove ${member.email} from this workspace?\nAny pending invitations for this person will also be invalidated.`,
          }),
    );
    if (!ok) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await api.removeOrgMember(orgId, member.id);
      await load(); // 본인 탈퇴면 재조회가 403 → forbidden 으로 정직하게 전환된다
    } catch (err) {
      fail(errText(err, t({ ko: '제거에 실패했습니다.', en: 'Could not remove the member.' })));
    } finally {
      setBusy(false);
    }
  }

  async function revokeInvitation(inv: OrgInvitation): Promise<void> {
    if (busy) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await api.revokeOrgInvitation(orgId, inv.id);
      await load();
    } catch (err) {
      // 409 = 그 사이 수락됨 — 최신 상태로 재조회한 뒤 서버 문구("멤버 제거를 쓰라")를 세운다.
      // load() 가 error 를 비우므로 순서를 뒤집으면 문구가 지워진다.
      if (err instanceof ApiError && err.status === 409) await load();
      fail(errText(err, t({ ko: '초대 취소에 실패했습니다.', en: 'Could not cancel the invitation.' })));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="org-members" data-testid="org-members" style={S.wrap}>
      <div style={S.head}>
        <h3 style={S.title}>{t({ ko: '워크스페이스 멤버', en: 'Workspace members' })}</h3>
        {phase === 'ready' ? (
          <span style={S.count}>{t({ ko: `${members.length}명`, en: `${members.length} members` })}</span>
        ) : null}
      </div>

      {phase === 'loading' ? (
        <p style={S.dim} role="status">
          {t({ ko: '불러오는 중…', en: 'Loading…' })}
        </p>
      ) : null}

      {phase === 'forbidden' ? (
        <p style={S.dim} data-testid="org-members-readonly">
          {t({ ko: '멤버 관리는 워크스페이스 관리자만 할 수 있습니다.', en: 'Only workspace admins can manage members.' })}
        </p>
      ) : null}

      {error ? (
        <p style={S.error} role="alert" data-testid="org-members-error">
          {error}
        </p>
      ) : null}

      {phase === 'error' ? (
        <div style={S.retryRow}>
          <button type="button" style={S.toolBtn} onClick={() => void load()} disabled={busy}>
            {t({ ko: '다시 시도', en: 'Try again' })}
          </button>
        </div>
      ) : null}

      {isAdmin && phase !== 'forbidden' ? (
        <>
          <form style={S.inviteRow} onSubmit={submitInvite} data-testid="org-members-invite">
            <input
              type="email"
              style={S.inviteInput}
              aria-label={t({ ko: '초대할 이메일', en: 'Email to invite' })}
              placeholder={t({ ko: '이메일로 초대', en: 'Invite by email' })}
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              disabled={locked}
              maxLength={320}
            />
            <select
              style={S.roleSelect}
              aria-label={t({ ko: '초대 역할', en: 'Invite role' })}
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value as OrgRole)}
              disabled={locked}
            >
              {invitableRoles.map((r) => (
                <option key={r} value={r}>
                  {t(ROLE_LABEL[r])}
                </option>
              ))}
            </select>
            <button type="submit" style={S.inviteBtn} disabled={locked || !inviteEmail.trim()}>
              {busy ? t({ ko: '처리 중…', en: 'Processing…' }) : t({ ko: '초대', en: 'Invite' })}
            </button>
          </form>
          {/* ★상시 고지 — 초대가 "메일 나감"으로 읽히는 순간 수신자는 영원히 기다린다. */}
          <p style={S.warn} data-testid="org-invite-mail-warning">
            {t({
              ko: '초대 메일은 아직 발송되지 않습니다. 초대 후 만들어지는 안내 문구를 복사해 직접 전달하세요.',
              en: "The invitation email hasn't been sent yet. Copy the guide text created after inviting and deliver it yourself.",
            })}
          </p>
        </>
      ) : null}

      {notice ? (
        <p style={S.notice} role="status" data-testid="org-members-notice">
          {notice}
        </p>
      ) : null}

      {guide ? (
        <div style={S.guideBox} data-testid="org-invite-guide-box">
          <textarea
            readOnly
            style={S.guideText}
            aria-label={t({ ko: `${guide.email} 가입 안내 문구`, en: `Sign-up guide text for ${guide.email}` })}
            data-testid="org-invite-guide"
            value={guide.text}
            rows={guide.text.split('\n').length}
          />
          <div style={S.guideActions}>
            <button type="button" style={S.toolBtn} onClick={() => void copyGuide()}>
              {copied ? t({ ko: '복사됨', en: 'Copied' }) : t({ ko: '안내 문구 복사', en: 'Copy guide text' })}
            </button>
            <button type="button" style={S.toolBtn} onClick={() => setGuide(null)}>
              {t({ ko: '닫기', en: 'Close' })}
            </button>
          </div>
        </div>
      ) : null}

      {isAdmin && members.length > 0 ? (
        <ul style={S.list}>
          {members.map((m) => {
            // OWNER 행은 서버 규칙상 OWNER 만 만질 수 있다 — 뻔히 거부될 호출을 막는 UX 힌트.
            const ownerLocked = m.role === 'OWNER' && !isOwner;
            return (
              <li key={m.id} style={S.row} data-testid="org-member-row">
                <span style={S.who}>
                  <span style={S.email}>{m.email}</span>
                  {m.name ? <span style={S.name}>{m.name}</span> : null}
                  {m.userId === myUserId ? <span style={S.meBadge}>{t({ ko: '나', en: 'Me' })}</span> : null}
                </span>
                <select
                  style={S.roleSelect}
                  aria-label={t({ ko: `${m.email} 역할`, en: `${m.email} role` })}
                  value={m.role}
                  onChange={(e) => void changeRole(m, e.target.value as OrgRole)}
                  disabled={locked || ownerLocked}
                  title={
                    ownerLocked
                      ? t({ ko: '소유자 역할 변경은 소유자만 할 수 있습니다.', en: "Only an owner can change an owner's role." })
                      : undefined
                  }
                >
                  {ROLE_OPTIONS.map((r) => (
                    <option key={r} value={r} disabled={r === 'OWNER' && !isOwner}>
                      {t(ROLE_LABEL[r])}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  style={S.removeBtn}
                  aria-label={t({
                    ko: `${m.email} ${m.userId === myUserId ? '탈퇴' : '제거'}`,
                    en: `${m.email} ${m.userId === myUserId ? 'Leave' : 'Remove'}`,
                  })}
                  onClick={() => void removeMember(m)}
                  disabled={locked || ownerLocked}
                  title={
                    ownerLocked
                      ? t({ ko: '소유자 제거는 소유자만 할 수 있습니다.', en: 'Only an owner can remove an owner.' })
                      : undefined
                  }
                >
                  {m.userId === myUserId ? t({ ko: '탈퇴', en: 'Leave' }) : t({ ko: '제거', en: 'Remove' })}
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}

      {isAdmin && phase === 'ready' ? (
        <div style={S.invSection}>
          <h4 style={S.subTitle}>
            {t({ ko: '초대 대기', en: 'Pending invites' })} {invitations.length > 0 ? `(${invitations.length})` : ''}
          </h4>
          {invitations.length === 0 ? (
            <p style={S.dim}>{t({ ko: '대기 중인 초대가 없습니다.', en: 'No pending invitations.' })}</p>
          ) : (
            <ul style={S.list}>
              {invitations.map((inv) => (
                <li key={inv.id} style={S.row} data-testid="org-invite-row">
                  <span style={S.who}>
                    <span style={S.email}>{inv.email}</span>
                    <span style={S.name}>{t(ROLE_LABEL[inv.role])}</span>
                    {/* 메일이 나가지 않으므로 "발송됨"으로 읽히면 안 된다. */}
                    <span
                      style={S.pending}
                      title={t({
                        ko: '초대 메일은 발송되지 않습니다 — 안내 문구를 직접 전달하세요.',
                        en: "The invitation email isn't sent — deliver the guide text yourself.",
                      })}
                    >
                      {t({ ko: '수동 통보 필요', en: 'Manual notice required' })}
                    </span>
                  </span>
                  <button
                    type="button"
                    style={S.toolBtn}
                    aria-label={t({ ko: `${inv.email} 안내 문구 보기`, en: `View guide text for ${inv.email}` })}
                    onClick={() => openGuide(inv.email)}
                    disabled={locked}
                  >
                    {t({ ko: '안내 문구', en: 'Guide text' })}
                  </button>
                  <button
                    type="button"
                    style={S.removeBtn}
                    aria-label={t({ ko: `${inv.email} 초대 취소`, en: `Cancel invitation for ${inv.email}` })}
                    onClick={() => void revokeInvitation(inv)}
                    disabled={locked}
                  >
                    {t({ ko: '초대 취소', en: 'Cancel invitation' })}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}

// FolderMembers.tsx 의 인라인 스타일 관용구를 따른다(같은 계열 화면, 토큰 동일).
const S: Record<string, CSSProperties> = {
  wrap: {
    border: '1px solid var(--color-border)',
    borderRadius: 10,
    padding: 14,
    marginBottom: 14,
    background: 'var(--surface-sunken)',
  },
  head: { display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 10 },
  title: { fontSize: 13, fontWeight: 700, color: 'var(--color-fg)', margin: 0 },
  subTitle: { fontSize: 12.5, fontWeight: 700, color: 'var(--color-fg)', margin: '12px 0 4px' },
  count: { fontSize: 12, color: 'var(--color-subtle)' },
  dim: { fontSize: 12.5, color: 'var(--color-subtle)', margin: '6px 0' },
  error: { fontSize: 12.5, color: 'var(--color-danger, #d92d20)', margin: '6px 0', lineHeight: 1.5 },
  warn: { fontSize: 12.5, color: 'var(--color-warning, #b54708)', margin: '0 0 10px', lineHeight: 1.5 },
  notice: {
    fontSize: 12.5,
    color: 'var(--color-fg)',
    background: 'var(--surface-hover)',
    border: '1px solid var(--color-border)',
    borderRadius: 7,
    padding: '7px 9px',
    margin: '0 0 10px',
    lineHeight: 1.5,
  },
  guideBox: {
    border: '1px dashed var(--color-border-strong)',
    borderRadius: 7,
    padding: 10,
    margin: '0 0 10px',
    background: 'var(--color-card)',
  },
  guideText: {
    width: '100%',
    fontSize: 12.5,
    fontFamily: 'inherit',
    lineHeight: 1.6,
    border: 'none',
    background: 'transparent',
    color: 'var(--color-fg)',
    resize: 'vertical',
  },
  guideActions: { display: 'flex', gap: 8, marginTop: 6 },
  retryRow: { margin: '6px 0 2px' },
  invSection: { marginTop: 4 },
  inviteRow: { display: 'flex', gap: 8, marginBottom: 8 },
  inviteInput: {
    flex: 1,
    minWidth: 0,
    fontSize: 13,
    padding: '7px 10px',
    borderRadius: 7,
    border: '1px solid var(--color-border-strong)',
    background: 'var(--color-card)',
    color: 'var(--color-fg)',
  },
  roleSelect: {
    fontSize: 13,
    padding: '7px 8px',
    borderRadius: 7,
    border: '1px solid var(--color-border-strong)',
    background: 'var(--color-card)',
    color: 'var(--color-fg)',
  },
  inviteBtn: {
    fontSize: 12.5,
    padding: '6px 12px',
    borderRadius: 7,
    border: 'none',
    background: 'var(--color-accent)',
    color: '#fff',
    cursor: 'pointer',
  },
  list: { listStyle: 'none', margin: 0, padding: 0 },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '7px 2px',
    borderTop: '1px solid var(--color-border)',
  },
  who: { flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' },
  email: { fontSize: 13, color: 'var(--color-fg)', overflow: 'hidden', textOverflow: 'ellipsis' },
  name: { fontSize: 11.5, color: 'var(--color-subtle)' },
  meBadge: {
    fontSize: 10.5,
    fontWeight: 700,
    color: 'var(--color-accent)',
    border: '1px solid var(--color-accent)',
    borderRadius: 5,
    padding: '1px 5px',
    whiteSpace: 'nowrap',
  },
  pending: {
    fontSize: 10.5,
    fontWeight: 700,
    color: 'var(--color-muted)',
    background: 'var(--surface-hover)',
    borderRadius: 5,
    padding: '2px 6px',
    whiteSpace: 'nowrap',
  },
  removeBtn: {
    fontSize: 12,
    padding: '5px 10px',
    borderRadius: 7,
    border: '1px solid var(--color-border-strong)',
    background: 'var(--color-card)',
    color: 'var(--color-danger, #d92d20)',
    cursor: 'pointer',
  },
  toolBtn: {
    fontSize: 12.5,
    padding: '6px 11px',
    borderRadius: 7,
    border: '1px solid var(--color-border-strong)',
    background: 'var(--color-card)',
    color: 'var(--color-fg)',
    cursor: 'pointer',
  },
};
